import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const theme = {
    isDarkMode,
    colors: isDarkMode ? {
      // Dark Mode Colors
      primary: '#6366F1',
      primaryHover: '#818CF8',
      secondary: '#8B5CF6',
      accent: '#F43F68',
      accentHover: '#FB7185',
      danger: '#F87171',
      warning: '#FBBF24',
      success: '#34D399',
      info: '#60A5FA',
      
      // Backgrounds
      background: '#0F172A',
      backgroundSecondary: '#1E293B',
      surface: '#1E293B',
      surfaceHover: '#334155',
      
      // Sidebar
      sidebarBg: '#0F1225',
      sidebarHover: '#1a1f36',
      sidebarText: '#64748B',
      sidebarTextActive: '#FFFFFF',
      sidebarActive: '#6366F1',
      
      // Text
      textPrimary: '#F1F5F9',
      textSecondary: '#CBD5E1',
      textTertiary: '#94A3B8',
      textMuted: '#64748B',
      
      // Borders
      border: '#334155',
      borderLight: '#1E293B',
      
      // Cards
      cardBg: '#1E293B',
      cardBorder: '#334155',
      
      // Status
      statusPending: '#60A5FA',
      statusInProgress: '#FBBF24',
      statusCompleted: '#34D399',
      
    } : {
      // Light Mode Colors - #241665 as primary
      primary: '#241665',
      primaryHover: '#1a1050',
      secondary: '#6366F1',
      accent: '#F43F68',
      accentHover: '#E11D48',
      danger: '#EF4444',
      warning: '#F59E0B',
      success: '#10B981',
      info: '#3B82F6',
      
      // Backgrounds
      background: '#F1F5F9',
      backgroundSecondary: '#E2E8F0',
      surface: '#FFFFFF',
      surfaceHover: '#F8FAFC',
      
      // Sidebar (dark even in light mode like Zoho)
      sidebarBg: '#1a1f36',
      sidebarHover: '#252b45',
      sidebarText: '#94A3B8',
      sidebarTextActive: '#FFFFFF',
      sidebarActive: '#241665',
      
      // Text
      textPrimary: '#0F172A',
      textSecondary: '#475569',
      textTertiary: '#64748B',
      textMuted: '#94A3B8',
      
      // Borders
      border: '#E2E8F0',
      borderLight: '#F1F5F9',
      
      // Cards
      cardBg: '#FFFFFF',
      cardBorder: '#E2E8F0',
      
      // Status
      statusPending: '#3B82F6',
      statusInProgress: '#F59E0B',
      statusCompleted: '#10B981',
    },
  };

  return (
    <ThemeContext.Provider value={{ ...theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
